import { useMemo, useRef, useState } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Button, IconButton, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { clsx } from 'clsx';

import type { PARENToken, RelationalFieldOption } from '../../../types/QueryBuilder';
import type { AutocompleteOption } from '../AutocompletePopover';
import { AutocompletePopover } from '../AutocompletePopover';
import { useQueryBuilderContext } from '../context';
import { useFilterBarContext } from '../FilterBarContext';
import { useAutocompleteNavigation } from '../hooks/useAutocompleteNavigation';
import { useQueryBuilderGridItem } from '../hooks/useQueryBuilderGridItem';
import { collectUsedExpressionTypes, dedupeOptionsByValue } from '../utils';

import styles from './LogicParen.module.css';

// Re-export so existing call sites that imported the type from `./tokens/LogicParen`
// keep working — the canonical home is now `../types`, which avoids cycles between
// FilterBar / FilterBarContext / LogicParen.
export type { RelationalFieldOption };

/**
 * Sentinel value used by the type-cell autocomplete's hardcoded "Logical Group"
 * option. Selecting it clears the EXPRESSION's `expressionType`, downgrading the
 * typed group back to a plain `(...)` parenthesised expression.
 */
const GROUP_SENTINEL = '__GROUP__';

/**
 * Tones assigned to the first N entries in `relationalFieldOptions` (index-based, so
 * the same entry always maps to the same colour regardless of its `value`). Entries
 * past the end of this palette fall through to the default neutral tone.
 */
const RELATIONAL_FIELD_COLOR_PALETTE = [
  'var(--lp-color-data-series-1)',
  'var(--lp-color-data-series-2)',
  'var(--lp-color-data-series-3)',
  'var(--lp-color-data-series-4)',
  'var(--lp-color-text-code-title)',
  'var(--lp-color-text-code-number)',
];

interface LogicParenProps {
  tokenKey: string;
  token: PARENToken;
  onRemove?: () => void;
  removable?: boolean;
  /**
   * The parent EXPRESSION's optional `expressionType` (the identifier glued to the
   * opening paren in the source query, e.g. `anySpan(...)` → `"anySpan"`). When set
   * the open and close parens take on the type's tone (--paren-color) and the close
   * paren renders an additional clickable cell that, on click, opens an autocomplete
   * letting the user pick a different type.
   */
  expressionType?: string;
  /**
   * Options surfaced in the type-cell autocomplete. When omitted, falls back to the
   * surrounding FilterBar's `relationalFieldOptions` (read from FilterBarContext) —
   * if neither is set, no options are shown and the type cell is non-interactive.
   */
  relationalFieldOptions?: RelationalFieldOption[];
}

export function LogicParen({
  tokenKey,
  token,
  onRemove,
  removable,
  expressionType,
  relationalFieldOptions: relationalFieldOptionsProp,
}: LogicParenProps) {
  const { activePopover, setActivePopover, dispatch, parsedQuery } = useQueryBuilderContext();
  const {
    readonly,
    gridState,
    relationalFieldOptions: relationalFieldOptionsCtx,
    autocompleteFooter,
    autocompleteDetails,
  } = useFilterBarContext();
  const relationalFieldOptions = relationalFieldOptionsProp ?? relationalFieldOptionsCtx ?? [];
  const usedExpressionTypes = collectUsedExpressionTypes(parsedQuery);
  const { containerProps, cellProps } = useQueryBuilderGridItem(tokenKey, gridState);
  const [activePart, setActivePart] = useState<'remove' | null>(null);
  const typeButtonRef = useRef<HTMLButtonElement>(null);

  const parenChar = token.side === 'open' ? '(' : ')';
  // Hide the type badge entirely in readonly mode: the menu it opens is interactive
  // by definition, so a readonly paren should display only the literal character.
  const showTypeBadge =
    !readonly && token.side === 'close' && relationalFieldOptions.length > 0 && Boolean(expressionType);
  // Guard by readonly so that a readonly paren rendering the same parsedQuery as
  // an editable one doesn't mirror its open popover — `activePopover` is shared via
  // QueryBuilderContext.
  const isThisActive = !readonly && activePopover?.tokenKey === tokenKey;
  const isTypePopoverOpen = isThisActive && activePopover?.part === 'type';
  // Prefer the option's display label when one is configured for the active type.
  const activeRelationalOption = relationalFieldOptions.find((o) => o.value === expressionType);
  const typeLabel = activeRelationalOption?.displayName ?? expressionType ?? '';
  const typeTooltip = (
    <>
      <strong>
        <Message id="queryBuilder.expressionGroup.relationalFilterTooltip" values={{ type: typeLabel }} />
      </strong>
      {activeRelationalOption?.description ? (
        <>
          <br />
          {activeRelationalOption.description}
        </>
      ) : null}
    </>
  );

  // Color both parens according to the prefix tone. Tones come from the palette in
  // the order the configured relational fields are listed — index 0 → series-1,
  // index 1 → series-2, etc. Entries beyond the palette length (and untyped
  // expressions) fall back to the default neutral tone.
  const typeIndex = expressionType ? relationalFieldOptions.findIndex((o) => o.value === expressionType) : -1;
  const typeColor =
    typeIndex >= 0 && typeIndex < RELATIONAL_FIELD_COLOR_PALETTE.length
      ? RELATIONAL_FIELD_COLOR_PALETTE[typeIndex]
      : undefined;
  const containerStyle = typeColor ? ({ '--paren-color': typeColor } as React.CSSProperties) : undefined;

  function handleTypeSelect(selected: string | number | boolean) {
    // The hardcoded "Group" option clears the prefix, downgrading the group back
    // to an untyped `(...)` expression. Any other selection writes its `value`
    // through as the new prefix — except when that type is already used elsewhere
    // in the query (the option is rendered disabled; this guard catches the
    // keyboard-Enter path that bypasses react-aria's disabled handling).
    if (selected !== GROUP_SENTINEL && selected !== expressionType && usedExpressionTypes.has(String(selected))) {
      return;
    }
    const newType = selected === GROUP_SENTINEL ? undefined : String(selected);
    dispatch({
      type: 'UPDATE_EXPRESSION_TYPE',
      token: token.expression,
      expressionType: newType,
    });
    setActivePopover(null);
  }

  // Lead the autocomplete with a hardcoded "Group" entry that has no associated
  // colour — selecting it clears the typed prefix. Subsequent entries are the
  // configured relational fields, in their original order so the index-based
  // colour palette assignment stays stable.
  //
  // Per-option flags:
  //   • disabled — already in use elsewhere in the query (and not the active
  //     type for this paren, which the user is free to re-pick as a no-op).
  //   • trailingIconName — green check on the option that matches the current
  //     `expressionType`, so the active type reads as "selected" in the menu.
  // Dedupe so the rendered rows, the arrow-key optionCount, and the Enter onSelect
  // index share one list — a relational field option whose value collides with the
  // group sentinel (or another field) would otherwise desync focus from selection.
  const typeAutocompleteOptions = dedupeOptionsByValue([
    {
      value: GROUP_SENTINEL,
      displayName: msg('queryBuilder.expressionGroup.logicalGroup'),
      description: msg('queryBuilder.expressionGroup.logicalGroupDescription'),
      ...(!expressionType ? { trailingIconName: 'check-circle' as const, trailingIconTone: 'success' as const } : {}),
    },
    // Non-creatable options (creatable === false) still label/scope hydrated groups but are
    // withheld from the change-type menu so no new group of that type can be created.
    ...relationalFieldOptions
      .filter((o) => o.creatable !== false)
      .map((o) => {
        const inUseElsewhere = usedExpressionTypes.has(o.value) && o.value !== expressionType;
        const isCurrent = o.value === expressionType;
        return {
          ...o,
          ...(inUseElsewhere ? { disabled: true } : {}),
          ...(isCurrent ? { trailingIconName: 'check-circle' as const, trailingIconTone: 'success' as const } : {}),
        };
      }),
  ]);

  const renderDetails = useMemo(() => {
    if (!autocompleteDetails) {
      return undefined;
    }
    return (option: AutocompleteOption) => {
      if (option.value === GROUP_SENTINEL) {
        // Reported as `GROUP`, the same identity the add-term list uses for plain grouping, so a
        // consumer describes an untyped group one way whether it is being added or swapped in here.
        return autocompleteDetails({
          part: 'logic',
          value: 'GROUP',
          displayName: msg('queryBuilder.expressionGroup.logicalGroup'),
          description: msg('queryBuilder.expressionGroup.logicalGroupDescription'),
        });
      }
      const relationalOption = relationalFieldOptions.find((candidate) => candidate.value === option.value);
      return relationalOption
        ? autocompleteDetails({
            part: 'relational',
            value: relationalOption.value,
            displayName: option.displayName,
            relationalFieldOption: relationalOption,
          })
        : null;
    };
  }, [autocompleteDetails, relationalFieldOptions]);

  const { focusedIndex, enterFocusedIndex, containerDataProps, handleKeyDown } = useAutocompleteNavigation({
    optionCount: typeAutocompleteOptions.length,
    isOpen: isTypePopoverOpen,
    onSelect: (index) => handleTypeSelect(typeAutocompleteOptions[index].value),
    onClose: () => {
      setActivePopover(null);
      requestAnimationFrame(() => typeButtonRef.current?.focus());
    },
  });

  return (
    // oxlint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- wrapper exists only to stop click propagation; interactive controls live inside
    <div className={styles.container} onClick={(e) => e.stopPropagation()}>
      {/* oxlint-disable-next-line jsx-a11y/no-static-element-interactions -- role="row" is provided via containerProps spread */}
      <div
        {...containerProps}
        {...containerDataProps}
        onKeyDown={isTypePopoverOpen ? handleKeyDown : undefined}
        onKeyDownCapture={(e) => {
          containerProps.onKeyDownCapture?.(e);
          const acceptIndex =
            e.key === 'Tab' && !e.shiftKey ? focusedIndex : e.key === 'Enter' ? enterFocusedIndex : -1;
          if (isTypePopoverOpen && acceptIndex >= 0) {
            e.preventDefault();
            e.stopPropagation();
            handleTypeSelect(typeAutocompleteOptions[acceptIndex].value);
          }
        }}
        className={styles.logicParen}
        style={containerStyle}
      >
        <div {...cellProps}>
          <span
            className={clsx(styles.paren, {
              [styles.rightParen]: token.side === 'close',
              [styles.cellFocusedPrev]: activePart === 'remove',
            })}
            aria-hidden
          >
            {parenChar}
          </span>
        </div>

        {showTypeBadge && (
          <div {...cellProps}>
            <TooltipTrigger delay={500} isDisabled={isTypePopoverOpen}>
              <Button
                ref={typeButtonRef}
                data-part="type"
                className={clsx(styles.typeBadge, {
                  [styles.typeBadgeActive]: isTypePopoverOpen,
                })}
                aria-label={msg('queryBuilder.expressionGroup.typeLabel', { type: typeLabel })}
                onPress={() => {
                  setActivePopover(isTypePopoverOpen ? null : { tokenKey, part: 'type' });
                }}
                onBlur={
                  isTypePopoverOpen
                    ? () => {
                        // Close the type popover when focus leaves the trigger button.
                        // Selecting an option fires onPress on the listbox item and triggers
                        // its own setActivePopover(null), so this handler only needs to clean
                        // up the case where the user tabs / clicks somewhere unrelated.
                        setActivePopover(null);
                      }
                    : undefined
                }
              >
                <span className={styles.typeBadgeIcon} aria-hidden="true">
                  <Icon name="filter" size="small" />
                </span>
                <span className={styles.typeBadgeLabel}>{typeLabel}</span>
              </Button>
              <Tooltip>{typeTooltip}</Tooltip>
            </TooltipTrigger>
          </div>
        )}

        {token.side === 'close' && removable && (
          <div {...cellProps}>
            <TooltipTrigger delay={500}>
              <IconButton
                className={clsx(styles.removeButton, {
                  [styles.cellFocused]: activePart === 'remove',
                })}
                onFocus={() => setActivePart('remove')}
                onBlur={() => setActivePart(null)}
                icon="cancel"
                variant="minimal"
                size="small"
                aria-label={msg('queryBuilder.expressionGroup.removeLabel')}
                onPress={onRemove}
              />
              <Tooltip>
                {expressionType ? (
                  <Message id="queryBuilder.expressionGroup.removeRelationalTooltip" />
                ) : (
                  <Message id="queryBuilder.expressionGroup.removeGroupTooltip" />
                )}
              </Tooltip>
            </TooltipTrigger>
          </div>
        )}
      </div>

      {isTypePopoverOpen && (
        <AutocompletePopover
          triggerRef={typeButtonRef}
          anchorKey={`${tokenKey}-type`}
          isOpen
          onOpenChange={(open) => !open && setActivePopover(null)}
          onSelect={handleTypeSelect}
          options={typeAutocompleteOptions}
          focusedIndex={focusedIndex}
          renderDetails={renderDetails}
          footer={autocompleteFooter}
        />
      )}
    </div>
  );
}

LogicParen.displayName = 'LogicParen';
